/**
 * Enotna pretvorba HTML/označenega vnosa v čisto besedilo.
 * Uporablja se pri:
 *  - urejanju izdelkov (sanitizacija ob nalaganju v urejevalnik),
 *  - shranjevanju izdelkov (server fn pred zapisom v bazo),
 *  - prikazu izdelkov (kombinirani opis in SEO meta),
 *  - migracijah (logika usklajena z SQL čistilnikom).
 *
 * Pravila:
 *  - blokovne oznake (br, /p, /div, /li, /h1..h6, /tr) postanejo prelom vrstice,
 *  - HTML komentarji in ostanki "-->" se odstranijo,
 *  - preostale oznake se odstranijo,
 *  - dekodirajo se pogoste imenovane in številčne entitete,
 *  - presledki se strnejo, prelomi ohranijo, največ dve zaporedni prazni vrsti,
 *  - rezultat se obreže na podano dolžino.
 */
const NAMED_ENTITIES: Record<string, string> = {
  nbsp: " ", amp: "&", lt: "<", gt: ">", quot: '"', apos: "'",
  hellip: "…", mdash: "—", ndash: "–",
  lsquo: "\u2018", rsquo: "\u2019", ldquo: "\u201C", rdquo: "\u201D",
  laquo: "«", raquo: "»", bull: "•", middot: "·",
  copy: "©", reg: "®", trade: "™",
};

export function cleanHtmlToText(input: string | null | undefined, max = 10000): string {
  if (!input) return "";
  let text = String(input);

  // 1. Odstrani HTML komentarje in osamljene zapiralne sekvence.
  text = text.replace(/<!--[\s\S]*?-->/g, " ").replace(/-->/g, " ");

  // 2. Blokovne oznake pretvori v prelome vrstic.
  text = text
    .replace(/<\s*br\s*\/?\s*>/gi, "\n")
    .replace(/<\s*\/\s*(p|div|li|h[1-6]|tr)\s*>/gi, "\n");

  // 3. Odstrani preostale HTML oznake.
  text = text.replace(/<[^>]+>/g, " ");

  // 4. Dekodiraj imenovane entitete.
  text = text.replace(/&([a-zA-Z]+);/g, (m, name: string) => {
    const v = NAMED_ENTITIES[name.toLowerCase()];
    return v !== undefined ? v : " ";
  });

  // 5. Dekodiraj številčne entitete (decimalne in šestnajstiške).
  text = text.replace(/&#(\d+);/g, (_m, n: string) => {
    const cp = parseInt(n, 10);
    return Number.isFinite(cp) && cp > 0 && cp <= 0x10ffff ? String.fromCodePoint(cp) : " ";
  });
  text = text.replace(/&#x([0-9a-fA-F]+);/g, (_m, n: string) => {
    const cp = parseInt(n, 16);
    return Number.isFinite(cp) && cp > 0 && cp <= 0x10ffff ? String.fromCodePoint(cp) : " ";
  });

  // 6. Normalizacija presledkov in prelomov.
  text = text
    .replace(/\r\n?/g, "\n")
    .replace(/[ \t\u00A0]+/g, " ")
    .replace(/[ \t]*\n[ \t]*/g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();

  if (text.length > max) text = text.slice(0, max).trimEnd();
  return text;
}
